import { useCallback, useContext, useEffect } from 'react';
import { ExtendedOrderCache } from '../../../lib/cache/ExtendedOrderCache';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { useVisible } from '~/apps/smartsend/hooks/useVisible';
import {
  SmartSendItemsAddedSocketAction,
  SmartSendItemsRemovedSocketAction
} from '~/apps/smartsend/lib/context/SmartSendContextModels';
import { SmartSendSubType } from '~/apps/smartsend/lib/consts/SmartSendSubType';
import { SmartSendItemsCache } from '~/apps/smartsend/lib/cache/SmartSendItemsCache';
import { chunk } from '~/apps/smartsend/lib/helpers/ArrayHelper';
import { IsSmartSend } from '~/apps/smartsend/lib/helpers/SmartSendSummaryGenerator';
import { SmartSendContext } from '~/apps/smartsend/lib/context/SmartSendContext';

const useSocketBackupBackgroundTask = () => {
  const [windowInFocus] = useVisible();
  const { getItems, getItemsByIds } = useSmartSendApi();
  const { dispatch: sctxDispatch } = useContext(SmartSendContext);

  const chunkGetItems = useCallback(
    async (type: 'SOLD', ids: { Id: string }[], size) => {
      const existingIds = SmartSendItemsCache.filter(IsSmartSend).map((x) => x.Id);

      const chunks = chunk(
        ids.filter((x) => !existingIds.includes(x.Id)).map((x) => x.Id),
        size
      );

      for (const c of chunks) {
        const res = await getItemsByIds(c);
        if (res.success) {
          switch (type) {
            case 'SOLD':
              sctxDispatch(new SmartSendItemsAddedSocketAction(res.result.Items, chunks.indexOf(c)));
              break;
          }
        }
      }
    },
    [getItemsByIds, sctxDispatch]
  );

  const updateItems = useCallback(async () => {
    const res = await getItems();

    if (res.success) {
      const newItems = res.result.Items.filter((x) => {
        return !SmartSendItemsCache.find((i) => i.Id === x.Id);
      });

      if (newItems.length > 0) {
        console.log(`BGTASK: Refreshing new items ${newItems.length}`);
        await chunkGetItems('SOLD', newItems, 250);
      }

      const oldItems = SmartSendItemsCache.filter(
        (i) =>
          i.SubType == SmartSendSubType &&
          !res.result.Items.find((x) => x.Id === i.Id) &&
          !ExtendedOrderCache.recentlyBookedSmartSendItems.includes(i.Id)
      );

      if (oldItems.length > 0) {
        console.log(`BGTASK: Refreshing removed items ${oldItems.length}`);
        sctxDispatch(new SmartSendItemsRemovedSocketAction(oldItems, 0));
      }
    }
  }, [getItems, chunkGetItems, sctxDispatch]);

  useEffect(() => {
    // Maintain interval across window focus changes
    const run = async () => {
      if (!windowInFocus) return;
      console.log('BGTASK: Getting Support Data as window in focus');

      try {
        await updateItems();
      } catch (error) {
        console.error('BGTASK: Error fetching items:', error);
      }
    };

    run();
    const interval = setInterval(run, 120000);
    return () => clearInterval(interval);
  }, [updateItems, windowInFocus]);
};

export default useSocketBackupBackgroundTask;
